import MediaLibraryClient from '../clients/MediaLibraryClient';
import MediaLibraryCollectionStorage from './storage-collection';
import MediaLibraryArchiveStorage from './storage-archive';
import MediaLibraryArchiveDownload from './download-archive';
import MediaLibraryAsset from './asset';
import MediaLibraryEvents from './events';
import Mustache from 'mustache';
import { getTranslation } from '../utilities';

class MediaLibraryCollectionComponent
{
    constructor()
    {
        // Imports
        this._client = new MediaLibraryClient();
        this._collectionStorage = new MediaLibraryCollectionStorage();
        this._archiveStorage = new MediaLibraryArchiveStorage();
        this._archiveDownload = new MediaLibraryArchiveDownload();
        this._asset = new MediaLibraryAsset();

        // Elements
        this._tableWrapper =
            document.querySelector('.js-media-library__table-wrapper');
        this._tableContainer =
            document.querySelector('.js-media-library__table-container');
        this._deleteSelectionButton =
            document.querySelector('.js-media-library__button--delete-selection');
        this._selectAllButton = undefined;
        this._assetTemplate =
            document.querySelector('#media-library-listview-item-element-template').innerHTML;
        this._searchInput =
            document.querySelector('.js-media-library__collection-search--input');
        this._collectionEmpty =
            document.querySelector('.js-media-library__tiles--no-results');
        this._loadingIndicator =
            document.querySelector('.js-media-library__table-wrapper .js-loading-indicator');

        // Download Elements
        this._selectDownloadFormats =
            document.querySelectorAll('.js-media-library__checkbox--download-format');
        this._selectDownloadFormatVideo =
            document.querySelector('.js-media-library__checkbox--download-format--video');
        this._selectDownloadFormatThreeSixty =
            document.querySelector('.js-media-library__checkbox--download-format--threesixty');
        this._selectDownloadFormatPng =
            document.querySelector('.js-media-library__checkbox--download-format--png');
        this._selectDownloadFormatJpg =
            document.querySelector('.js-media-library__checkbox--download-format--jpg');
        this._selectDownloadFormatPsd =
            document.querySelector('.js-media-library__checkbox--download-format--psd');
        this._selectDownloadFormatSvg =
            document.querySelector('.js-media-library__checkbox--download-format--svg');
        this._selectDownloadFormatEps =
            document.querySelector('.js-media-library__checkbox--download-format--eps');
        this._prepareSelectionButton =
            document.querySelector('.js-media-library__button--prepare-selection');
        this._prepareSelectionOverlayButton =
            document.querySelector('.js-media-library__button--prepare-selection--overlay-trigger');

        // Selectors
        this._tableSelector = '.js-media-library-collection-table';
        this._selectAllButtonSelector = '.js-media-library__checkbox--select-all';

        // State Classes
        this._isVideoClass = ('js-media-library__tile--is-video');
        this._isThreeSixtyClass = ('js-media-library__tile--is-threesixty');
        this._isImageClass = ('js-media-library__tile--is-image');
        this._isLogoClass = ('js-media-library__tile--is-logo');

        // Events
        this._removeFromCollectionEvent = new Event(MediaLibraryEvents.removeFromCollectionEvent);
        this._assetSelectedEvent = new Event(MediaLibraryEvents.assetSelectedEvent);
        this._noAssetSelectedEvent = new Event(MediaLibraryEvents.noAssetSelectedEvent);

        // Helpers
        this._table = null;
        this._customerId = orion.getCustomerId();
        this._assetsPerPage = 30;
        this._maxAssets = 1000;
        this._selectedFormats = [];
        this._downloadPath = 'https://downloads.orion-wholesale.com/prepared/';
    }

    init()
    {
        if (this._collectionStorage.hasCollection(this._customerId))
        {
            this.initCollection();
        }
        else
        {
            this.handleEmptyCollection();
        }
    }

    async initCollection()
    {
        this._collectionStorage.restoreCollection(this._customerId);

        if (this._collectionStorage.getCollection().length)
        {
            this._loadingIndicator.classList.remove('loading-indicator--hidden');

            let rawAssetData = await this.fetchAssets();

            let processedAssetData = this._asset.processAssetData(rawAssetData);

            this.initTable(processedAssetData);
        }
        else
        {
            this.handleEmptyCollection();
        }
    }

    /**
     * @return {Object} Raw asset data
     */
    fetchAssets()
    {
        return this._client.fetchAssetsByAssetIds(
            this._collectionStorage.getCollection(),
            this._maxAssets,
            0
        )
            .then(response => response.json())
            .then(json =>
            {
                if (this._archiveStorage.checkStorage(this._customerId))
                {
                    this.initDownload();
                }

                return json;
            });
    }

    /**
     * @param {Object} assetData Processed asset data
     */
    initTable(assetData)
    {
        this._table = $(this._tableSelector).DataTable({
            data: assetData,
            pageLength: 20,
            dom: 'rtpi',
            language: this.getDatatableTexts(),
            order: [],
            select: {
                style: 'multi',
                selector: 'td:first-child'
            },
            columnDefs: [
                {
                    targets: [0, 1, 2, 3, 4, 5, 6, 7, 8],
                    orderable: false
                },
            ],
            columns: [
                {
                    // Hidden column to sort for initial index
                    // Sort criteria must be mapped to a column
                    targets: 0,
                    visible: false,
                    render: (data, type, row, meta) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            index: meta.row,
                            indexColumn: true
                        });
                    }
                },
                {
                    width: '5%',
                    targets: 1,
                    className: 'select-checkbox',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            checkColumn: true
                        });
                    }
                },
                {
                    // Hidden column to sort for product number
                    // Sort criteria must be mapped to a column
                    targets: 2,
                    visible: false,
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            productNumberColumn: true
                        });
                    }
                },
                {
                    // Hidden column to sort for product label
                    // Sort criteria must be mapped to a column
                    targets: 3,
                    visible: false,
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            labelColumn: true
                        });
                    }
                },
                {
                    width: '37%',
                    targets: 4,
                    className: 'media-library-collection__table__column__details',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            descriptionColumn: true
                        });
                    }
                },
                {
                    width: '10%',
                    targets: 5,
                    className: 'media-library-collection__table__column__uploaded',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            dataUploaded: row.addedTimeStamp.replace(/\D/g, '')
                                .replace(/(\d{2})(\d{2})(\d{4})/, '$3$2$1'),
                            uploadedColumn: true
                        });
                    }
                },
                {
                    width: '10%',
                    data: null,
                    targets: 6,
                    className: 'media-library-collection__table__column__changed',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            dataChanged: row.updatedTimeStamp.replace(/\D/g, '')
                                .replace(/(\d{2})(\d{2})(\d{4})/, '$3$2$1'),
                            changedColumn: true
                        });
                    }
                },
                {
                    width: '10%',
                    targets: 7,
                    className: 'media-library-collection__table__column__dimensions',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            dimensionsColumn: true
                        });
                    }
                },
                {
                    width: '13%',
                    targets: 8,
                    className: 'media-library-collection__table__column__file-formats',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            fileFormatsColumn: true
                        });
                    }
                },
                {
                    width: '15%',
                    targets: 9,
                    className: 'media-library-collection__table__column__download',
                    render: (data, type, row) =>
                    {
                        return Mustache.render(this._assetTemplate, {
                            mediaCollectionAsset: row,
                            downloadColumn: true
                        });
                    }
                }
            ],
            createdRow: (row, data) =>
            {
                let classMap = [
                    { key: 'videoFilePath', class: this._isVideoClass },
                    { key: 'archiveFilePath', class: this._isThreeSixtyClass },
                    { key: 'psdFilePath', class: this._isImageClass },
                    { key: 'svgFilePath', class: this._isLogoClass }
                ];

                classMap.forEach(({ key, class: className }) =>
                {
                    if (data[key])
                    {
                        $(row).addClass(className);
                    }
                });

                $(row).find('.js-media-library__asset-template--image').attr('src', data.thumbnail);

                this._asset.initEvents(row);
            },
            initComplete: () =>
            {
                this._tableContainer.classList.remove('hidden');
                this._loadingIndicator.classList.add('loading-indicator--hidden');
                this._archiveDownload.init(
                    this.getSelectedAssetData.bind(this),
                    this.enableActionButtons.bind(this),
                    this.disableActionButtons.bind(this)
                );
            },
            drawCallback: () =>
            {
                this._selectAllButton = document.querySelector(this._selectAllButtonSelector);
            }
        });

        this.createSortSelection();
        this.initEvents();
    }

    createSortSelection()
    {
        let filterSection = document.createElement('div');
        let sortSelection = document.createElement('select');
        let sortPlaceholder = document.createElement('option');

        filterSection.classList.add('media-library__collection-sort', 'select-button-container');

        sortPlaceholder.value = '';
        sortPlaceholder.text = getTranslation('content.media.library.sort.recent');
        sortPlaceholder.selected;
        sortPlaceholder.disabled;
        sortSelection.appendChild(sortPlaceholder);

        let { 0: columns } = this._table.columns();

        for (let i = 1; i < columns.length; i++)
        {
            // 0=index, 1=Checkbox, 7=Dimensions, 8=Format, 9=Download
            if (i === 0 || i === 1 || i === 7 || i === 8 || i === 9)
            {
                continue;
            }

            let optionAscending = document.createElement('option');
            let optionDescending = document.createElement('option');

            optionAscending.value = columns[i];
            optionAscending.text = getTranslation('content.media.library.header.col.' + i) + ' '
                + getTranslation('content.media.library.sort.ascending');
            sortSelection.appendChild(optionAscending);

            optionDescending.value = columns[i] * -1;
            optionDescending.text = getTranslation('content.media.library.header.col.' + i) + ' '
                + getTranslation('content.media.library.sort.descending');
            sortSelection.appendChild(optionDescending);
        }

        filterSection.append(sortSelection);
        document.querySelector('.js-media-library__sort-container').append(filterSection);

        sortSelection.addEventListener('change', event =>
        {
            if (event.target.value > 0)
            {
                this._table.order([event.target.value, 'asc']).draw();
            }
            else if (event.target.value < 0)
            {
                this._table.order([event.target.value * -1, 'desc']).draw();
            }
            else
            {
                this._table.order([0, 'asc']).draw();
            }
        });
    }

    initEvents()
    {
        this._searchInput.addEventListener('input', () =>
        {
            this._table.search(this._searchInput.value).draw();
        });

        $(this._selectAllButtonSelector).on('click', () =>
        {
            let checkbox = $(this._selectAllButtonSelector);

            if (!checkbox.hasClass('selected'))
            {
                this._table.rows().select();

                checkbox.addClass('selected');
            }
            else
            {
                this._table.rows().deselect();

                checkbox.removeClass('selected');
            }
        });

        $(this._deleteSelectionButton).on('click', () =>
        {
            this.deleteSelection();
            this._deleteSelectionButton.classList.add('disabled');
        });

        this._table.on('select deselect', (e, dt, type, indexes) =>
        {
            let countSelectedFalse = this._table.rows({ selected: false }).count();
            let countSelectedTrue = this._table.rows({ selected: true }).count();

            this._selectAllButton.classList.toggle('selected', !countSelectedFalse);
            this._deleteSelectionButton.classList.toggle('disabled', !countSelectedTrue);

            if (countSelectedTrue)
            {
                if (e.type === 'select')
                {
                    let formatMap = {
                        [this._isVideoClass]: this._selectDownloadFormatVideo,
                        [this._isThreeSixtyClass]: this._selectDownloadFormatThreeSixty,
                        [this._isImageClass]: this._selectDownloadFormatJpg,
                        [this._isLogoClass]: this._selectDownloadFormatSvg
                    };

                    indexes.forEach(index =>
                    {
                        let row = this._table.row(index).node();

                        for (let [className, checkbox] of Object.entries(formatMap))
                        {
                            if (
                                row.classList.contains(className) &&
                                (className !== this._isImageClass ||
                                (!this._selectDownloadFormatPng.checked &&
                                !this._selectDownloadFormatPsd.checked)) &&
                                (className !== this._isLogoClass ||
                                !this._selectDownloadFormatEps.checked)
                            )
                            {
                                checkbox.checked = true;
                                checkbox.dispatchEvent(new Event('change', { bubbles: true }));
                                break;
                            }
                        }
                    });
                }

                if (!this._table.rows({ selected: true }).data().toArray().some(asset => asset.jpgFilePath))
                {
                    this._selectDownloadFormatPng.checked = false;
                    this._selectDownloadFormatJpg.checked = false;
                    this._selectDownloadFormatPsd.checked = false;

                    this._selectDownloadFormatJpg.dispatchEvent(new Event('change', { bubbles: true }));
                }

                if (!this._table.rows({ selected: true }).data().toArray().some(asset => asset.svgFilePath)
                )
                {
                    this._selectDownloadFormatSvg.checked = false;
                    this._selectDownloadFormatEps.checked = false;

                    this._selectDownloadFormatSvg.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
            else
            {
                this._selectDownloadFormats.forEach(format =>
                {
                    format.checked = false;
                });
            }

            document.dispatchEvent(countSelectedTrue ? this._assetSelectedEvent : this._noAssetSelectedEvent);
        });

        this._table.on('page.dt', () =>
        {
            window.scrollTo(0, 0);
        });
    }

    initDownload()
    {
        let storage = this._archiveStorage.getStorage(this._customerId);

        this._client.fetchProgressJson(this._downloadPath + storage.jsonPath)
            .then(() =>
            {
                let rows = this._table.rows();

                rows.data().each((rowData, rowIndex) =>
                {
                    if (storage.assets.includes(rowData.assetId))
                    {
                        this._table.row(rowIndex).select();
                    }
                });

                this._selectDownloadFormats.forEach(checkbox =>
                {
                    checkbox.checked = storage.fileFormats.includes(checkbox.value);
                });

                this._archiveDownload.checkDownloadStatus(storage.jsonPath);
            })
            .catch(() =>
            {
                this._archiveStorage.removeStorage(this._customerId);
            });
    }

    deleteSelection()
    {
        if (this._collectionStorage.getCollection().length > this._table.rows({ selected: true }).count())
        {
            let assets = this._table.rows({ selected: true }).data();

            for (let i = 0; i < assets.length; i++)
            {
                this._collectionStorage.removeFromCollection(assets[i].assetId);
            }

            this._collectionStorage.saveCollection(this._customerId);

            this._table.rows({ selected: true }).remove().draw();
        }
        else
        {
            this._collectionStorage.clearCollection(this._customerId);

            this._collectionStorage.removeTimestamp(this._customerId);

            this.handleEmptyCollection();
        }

        document.dispatchEvent(this._removeFromCollectionEvent);

        this.disableDownloadButtons();
    }

    disableDownloadButtons()
    {
        this._selectDownloadFormats.forEach(checkbox =>
        {
            checkbox.checked = false;
        });
        this._prepareSelectionButton.classList.add('disabled');
    }

    enableActionButtons()
    {
        this._table.rows().nodes().each(row =>
        {
            row.querySelector('td:first-child').classList.remove('disabled');
        });

        this._selectAllButton.classList.remove('disabled');
        this._deleteSelectionButton.classList.remove('disabled');
    }

    disableActionButtons()
    {
        let isArchiveInStorage = this._archiveStorage.checkStorage(this._customerId);

        this._table.rows().nodes().each(row =>
        {
            row.querySelector('td:first-child').classList.toggle('disabled', isArchiveInStorage);
        });

        this._prepareSelectionButton.classList.add('hidden');
        this._prepareSelectionOverlayButton.classList.add('hidden');
        this._selectAllButton.classList.add('disabled');
        this._deleteSelectionButton.classList.add('disabled');
    }

    handleEmptyCollection()
    {
        this._tableWrapper.innerText = '';

        let clone = this._collectionEmpty.cloneNode(true);

        clone.classList.remove('hidden');

        this._tableWrapper.append(clone);
    }

    /**
     * @return {Array} Selected asset data
     */
    getSelectedAssetData()
    {
        return this._table.rows({ selected: true }).data().toArray();
    }

    getDatatableTexts()
    {
        return {
            'paginate': {
                'previous': '<i class="fa-regular fa-chevron-left p3"></i>',
                'next': '<i class="fa-regular fa-chevron-right p3"></i>',
            },
            'processing': getTranslation('content.media.library.processing'),
            'loadingRecords': getTranslation('content.media.library.loading'),
            'info': getTranslation('content.media.library.info.entries'),
            'infoEmpty': getTranslation('content.media.library.info.entries.empty'),
            'emptyTable': getTranslation('content.media.library.info.list.empty'),
            'select': {
                'rows': {
                    _: '(%d) '
                        + getTranslation('content.media.library.info.selected.article'),
                },
            }
        };
    }
}

if (document.querySelector('#media-library-collection'))
{
    window.history.scrollRestoration = 'manual';

    const mediaLibraryCollectionComponent = new MediaLibraryCollectionComponent();

    mediaLibraryCollectionComponent.init();
}
